import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  MessageSquare,
  Send,
  Ticket,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Eye,
  Paperclip,
  X,
  File,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface TicketData {
  id: string
  ticket_number: string
  category: string
  priority: string
  status: string
  subject: string
  description: string
  created_at: string
  first_response_at: string | null
  resolved_at: string | null
  message_count?: number
}

interface TicketMessage {
  id: string
  author_name: string
  author_type: string
  message: string
  is_internal: boolean
  created_at: string
}

interface HelpSupportProps {
  user: AdminUser | null
}

export default function HelpSupport({ user }: HelpSupportProps) {
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [myTickets, setMyTickets] = useState<TicketData[]>([])
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null)
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([])
  const [showTicketDialog, setShowTicketDialog] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)

  const [formData, setFormData] = useState({
    category: 'technical',
    priority: 'medium',
    subject: '',
    description: '',
  })

  // File attachments
  const [attachments, setAttachments] = useState<File[]>([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)

  useEffect(() => {
    loadMyTickets()
  }, [user])

  async function loadMyTickets() {
    if (!user?.churchId) return

    try {
      setLoading(true)

      // Load tickets for this church
      const { data: tickets, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('church_id', user.churchId)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Count messages for each ticket
      const ticketsWithCounts = await Promise.all(
        (tickets || []).map(async (ticket) => {
          const { count } = await supabase
            .from('support_ticket_messages')
            .select('*', { count: 'exact', head: true })
            .eq('ticket_id', ticket.id)
            .eq('is_internal', false) // Only count public messages

          return {
            ...ticket,
            message_count: count || 0,
          }
        })
      )

      setMyTickets(ticketsWithCounts)
    } catch (error) {
      console.error('Error loading tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadTicketMessages(ticketId: string) {
    try {
      const { data, error } = await supabase
        .from('support_ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .eq('is_internal', false) // Church admins don't see internal notes
        .order('created_at', { ascending: true })

      if (error) throw error

      setTicketMessages(data || [])
    } catch (error) {
      console.error('Error loading messages:', error)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      // Generate ticket number
      const { data: ticketNumber, error: funcError } = await supabase.rpc('generate_ticket_number')

      if (funcError) throw funcError

      // Create ticket
      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          ticket_number: ticketNumber,
          church_id: user!.churchId,
          submitted_by: user!.id,
          submitter_name: `${user!.firstName} ${user!.lastName}`,
          submitter_email: user!.email,
          submitter_phone: user?.phone || null,
          category: formData.category,
          priority: formData.priority,
          subject: formData.subject,
          description: formData.description,
          status: 'new',
        })
        .select()
        .single()

      if (error) throw error

      // Upload attachments if any
      if (attachments.length > 0) {
        setUploadingAttachments(true)
        await uploadAttachments(ticket.id, user!.churchId)
      }

      alert(
        `Ticket Submitted Successfully!\n\nTicket Number: ${ticketNumber}\n\nOur support team will respond soon. You can track the status of your ticket below.`
      )

      // Reset form
      setFormData({
        category: 'technical',
        priority: 'medium',
        subject: '',
        description: '',
      })
      setAttachments([])

      // Reload tickets
      loadMyTickets()
    } catch (error: any) {
      console.error('Error submitting ticket:', error)
      alert('Failed to submit ticket: ' + error.message)
    } finally {
      setSubmitting(false)
      setUploadingAttachments(false)
    }
  }

  async function uploadAttachments(ticketId: string, churchId: string) {
    for (const file of attachments) {
      try {
        const filePath = `${churchId}/${ticketId}/${file.name}`

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('ticket-attachments')
          .upload(filePath, file)

        if (uploadError) throw uploadError

        // Create attachment record
        await supabase.from('support_ticket_attachments').insert({
          ticket_id: ticketId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: file.type,
          uploaded_by: user!.id,
        })
      } catch (error) {
        console.error('Error uploading attachment:', error)
      }
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      // Limit to 5MB per file
      const validFiles = files.filter((file) => {
        if (file.size > 5 * 1024 * 1024) {
          alert(`File "${file.name}" is too large. Maximum size is 5MB.`)
          return false
        }
        return true
      })
      setAttachments((prev) => [...prev, ...validFiles])
    }
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleViewTicket(ticket: TicketData) {
    setSelectedTicket(ticket)
    setShowTicketDialog(true)
    await loadTicketMessages(ticket.id)
  }

  async function handleSendMessage() {
    if (!newMessage.trim() || !selectedTicket) return

    setSending(true)

    try {
      // Add message
      const { error } = await supabase.from('support_ticket_messages').insert({
        ticket_id: selectedTicket.id,
        author_id: user!.id,
        author_type: 'customer',
        author_name: `${user!.firstName} ${user!.lastName}`,
        message: newMessage,
        is_internal: false,
      })

      if (error) throw error

      setNewMessage('')
      await loadTicketMessages(selectedTicket.id)
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  function getPriorityColor(priority: string) {
    switch (priority) {
      case 'critical':
        return 'bg-red-500'
      case 'high':
        return 'bg-orange-500'
      case 'medium':
        return 'bg-yellow-500'
      case 'low':
        return 'bg-blue-500'
      default:
        return 'bg-gray-500'
    }
  }

  function getStatusColor(status: string) {
    switch (status) {
      case 'new':
        return 'bg-blue-500'
      case 'open':
        return 'bg-purple-500'
      case 'in_progress':
        return 'bg-yellow-500'
      case 'waiting_on_customer':
        return 'bg-orange-500'
      case 'resolved':
        return 'bg-green-500'
      case 'closed':
        return 'bg-gray-500'
      default:
        return 'bg-gray-500'
    }
  }

  function formatStatus(status: string) {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <MessageSquare className="h-8 w-8" />
          Help & Support
        </h1>
        <p className="text-muted-foreground mt-1">Get help from our support team</p>
      </div>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Our support team typically responds within 24 hours for medium priority tickets. Critical
          issues are addressed within 1 hour.
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Create Ticket Form */}
        <Card>
          <CardHeader>
            <CardTitle>Submit a Support Request</CardTitle>
            <CardDescription>
              Need help? Fill out the form below and our team will get back to you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical Support</SelectItem>
                      <SelectItem value="billing">Billing Question</SelectItem>
                      <SelectItem value="feature_request">Feature Request</SelectItem>
                      <SelectItem value="bug_report">Bug Report</SelectItem>
                      <SelectItem value="general">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - General Question</SelectItem>
                      <SelectItem value="medium">Medium - Need Help</SelectItem>
                      <SelectItem value="high">High - Blocking Issue</SelectItem>
                      <SelectItem value="critical">Critical - System Down</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please provide as much detail as possible. Include steps to reproduce if it's a bug, or specific features you're requesting."
                  rows={8}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Include screenshots, error messages, or any other relevant information
                </p>
              </div>

              {/* File Attachments */}
              <div className="space-y-2">
                <Label htmlFor="attachments">Attachments (Optional)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="attachments"
                    type="file"
                    onChange={handleFileChange}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className="cursor-pointer"
                  />
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Max 5MB per file. Supported: Images, PDF, Word, Text
                </p>

                {attachments.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-muted rounded border"
                      >
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button type="submit" disabled={submitting || uploadingAttachments} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Ticket
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* My Tickets */}
        <Card>
          <CardHeader>
            <CardTitle>My Support Tickets</CardTitle>
            <CardDescription>
              {myTickets.length === 0
                ? 'No tickets submitted yet'
                : `${myTickets.length} ticket${myTickets.length === 1 ? '' : 's'}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : myTickets.length === 0 ? (
              <div className="text-center py-12">
                <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No tickets submitted yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Submit your first support request using the form
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {myTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="border rounded-lg p-4 hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => handleViewTicket(ticket)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold">
                          {ticket.ticket_number}
                        </span>
                        <Badge variant="outline" className={`${getPriorityColor(ticket.priority)} text-white`}>
                          {ticket.priority}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <p className="font-medium mb-2">{ticket.subject}</p>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`${getStatusColor(ticket.status)} text-white`}>
                        {formatStatus(ticket.status)}
                      </Badge>
                      <Badge variant="outline">
                        {ticket.category.replace('_', ' ')}
                      </Badge>
                      {ticket.message_count && ticket.message_count > 0 && (
                        <Badge variant="outline">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {ticket.message_count} {ticket.message_count === 1 ? 'reply' : 'replies'}
                        </Badge>
                      )}
                      {ticket.resolved_at && (
                        <Badge variant="outline" className="bg-green-500 text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Resolved
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ticket Details Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              {selectedTicket?.ticket_number} - {selectedTicket?.subject}
            </DialogTitle>
            <DialogDescription>
              Created {selectedTicket && new Date(selectedTicket.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant="outline" className={`${getStatusColor(selectedTicket.status)} text-white mt-1`}>
                    {formatStatus(selectedTicket.status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Priority</p>
                  <Badge variant="outline" className={`${getPriorityColor(selectedTicket.priority)} text-white mt-1`}>
                    {selectedTicket.priority}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="font-medium mt-1">{selectedTicket.category.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium mt-1">
                    {new Date(selectedTicket.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Original Description */}
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Original Request</p>
                <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Messages */}
              <div>
                <p className="text-sm font-medium mb-3">Conversation</p>
                {ticketMessages.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No responses yet. Our team will respond soon.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {ticketMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`p-3 rounded-lg ${
                          message.author_type === 'customer'
                            ? 'bg-blue-50 dark:bg-blue-950 ml-8'
                            : 'bg-muted mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{message.author_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Message (only if not closed) */}
              {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                <div className="space-y-2">
                  <Label>Add a reply</Label>
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message here..."
                    rows={4}
                  />
                  <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()}>
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
